import React, { useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { uploadImage } from '../../firebase/firebase-config';

// Custom Font Size Extension
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize,
        renderHTML: attributes => {
          if (!attributes.fontSize) {
            return {};
          }
          return {
            style: `font-size: ${attributes.fontSize}`,
          };
        },
      },
    };
  },
});

// Custom Line Height Extension
const LineHeight = TextStyle.extend({
  addAttributes() {
    return {
      lineHeight: {
        default: null,
        parseHTML: element => element.style.lineHeight,
        renderHTML: attributes => {
          if (!attributes.lineHeight) {
            return {};
          }
          return {
            style: `line-height: ${attributes.lineHeight}`,
          };
        },
      },
    };
  },
});

// Custom Letter Spacing Extension
const LetterSpacing = TextStyle.extend({
  addAttributes() {
    return {
      letterSpacing: {
        default: null,
        parseHTML: element => element.style.letterSpacing,
        renderHTML: attributes => {
          if (!attributes.letterSpacing) {
            return {};
          }
          return {
            style: `letter-spacing: ${attributes.letterSpacing}`,
          };
        },
      },
    };
  },
});

const RichTextEditor = ({ value, onChange, placeholder = "Write your content here...", label = "", imageFolder = "editor-images" }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [currentLineHeight, setCurrentLineHeight] = useState('1.5');
  const [currentLetterSpacing, setCurrentLetterSpacing] = useState('normal');
  const [currentFontFamily, setCurrentFontFamily] = useState('Arial');
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      LineHeight,
      LetterSpacing,
      HorizontalRule,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
      
      // Update current values
      const attrs = editor.getAttributes('textStyle');
      setCurrentFontSize(attrs.fontSize || '16px');
      setCurrentLineHeight(attrs.lineHeight || '1.5');
      setCurrentLetterSpacing(attrs.letterSpacing || 'normal');
      setCurrentFontFamily(attrs.fontFamily || 'Arial');
    },
    editorProps: {
      attributes: {
        class: 'rich-text-editor-content',
      },
    },
  });

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle preview mode
  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // Image upload handler
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const url = await uploadImage(file, `${imageFolder}/${Date.now()}_${file.name}`);
      if (editor) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  // Font size options - Expanded list
  const fontSizes = [
    { label: '8px', value: '8px' },
    { label: '9px', value: '9px' },
    { label: '10px', value: '10px' },
    { label: '11px', value: '11px' },
    { label: '12px', value: '12px' },
    { label: '13px', value: '13px' },
    { label: '14px', value: '14px' },
    { label: '15px', value: '15px' },
    { label: '16px', value: '16px' },
    { label: '17px', value: '17px' },
    { label: '18px', value: '18px' },
    { label: '19px', value: '19px' },
    { label: '20px', value: '20px' },
    { label: '22px', value: '22px' },
    { label: '24px', value: '24px' },
    { label: '26px', value: '26px' },
    { label: '28px', value: '28px' },
    { label: '30px', value: '30px' },
    { label: '32px', value: '32px' },
    { label: '34px', value: '34px' },
    { label: '36px', value: '36px' },
    { label: '38px', value: '38px' },
    { label: '40px', value: '40px' },
    { label: '42px', value: '42px' },
    { label: '44px', value: '44px' },
    { label: '46px', value: '46px' },
    { label: '48px', value: '48px' },
    { label: '50px', value: '50px' },
    { label: '52px', value: '52px' },
    { label: '54px', value: '54px' },
    { label: '56px', value: '56px' },
    { label: '58px', value: '58px' },
    { label: '60px', value: '60px' },
    { label: '64px', value: '64px' },
    { label: '68px', value: '68px' },
    { label: '72px', value: '72px' },
    { label: '80px', value: '80px' },
    { label: '96px', value: '96px' },
  ];

  // Font family options - More options
  const fontFamilies = [
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Arial Black', value: 'Arial Black, sans-serif' },
    { label: 'Calibri', value: 'Calibri, sans-serif' },
    { label: 'Cambria', value: 'Cambria, serif' },
    { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
    { label: 'Courier New', value: 'Courier New, monospace' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Helvetica', value: 'Helvetica, sans-serif' },
    { label: 'Impact', value: 'Impact, sans-serif' },
    { label: 'Lucida Sans', value: 'Lucida Sans, sans-serif' },
    { label: 'Montserrat', value: 'Montserrat, sans-serif' },
    { label: 'Open Sans', value: 'Open Sans, sans-serif' },
    { label: 'Poppins', value: 'Poppins, sans-serif' },
    { label: 'Roboto', value: 'Roboto, sans-serif' },
    { label: 'Segoe UI', value: 'Segoe UI, sans-serif' },
    { label: 'Tahoma', value: 'Tahoma, sans-serif' },
    { label: 'Times New Roman', value: 'Times New Roman, serif' },
    { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
  ];

  // Line height options
  const lineHeights = [
    { label: '1.0', value: '1' },
    { label: '1.1', value: '1.1' },
    { label: '1.2', value: '1.2' },
    { label: '1.3', value: '1.3' },
    { label: '1.4', value: '1.4' },
    { label: '1.5', value: '1.5' },
    { label: '1.6', value: '1.6' },
    { label: '1.7', value: '1.7' },
    { label: '1.8', value: '1.8' },
    { label: '1.9', value: '1.9' },
    { label: '2.0', value: '2' },
    { label: '2.2', value: '2.2' },
    { label: '2.5', value: '2.5' },
    { label: '3.0', value: '3' },
  ];

  // Letter spacing options
  const letterSpacings = [
    { label: 'Normal', value: 'normal' },
    { label: '-2px', value: '-2px' },
    { label: '-1px', value: '-1px' },
    { label: '-0.5px', value: '-0.5px' },
    { label: '0.5px', value: '0.5px' },
    { label: '1px', value: '1px' },
    { label: '1.5px', value: '1.5px' },
    { label: '2px', value: '2px' },
    { label: '2.5px', value: '2.5px' },
    { label: '3px', value: '3px' },
    { label: '4px', value: '4px' },
    { label: '5px', value: '5px' },
  ];

  // Heading sizes for quick access
  const headingSizes = [
    { label: 'H1', value: '32px', level: 1 },
    { label: 'H2', value: '28px', level: 2 },
    { label: 'H3', value: '24px', level: 3 },
    { label: 'H4', value: '20px', level: 4 },
    { label: 'H5', value: '18px', level: 5 },
    { label: 'H6', value: '16px', level: 6 },
    { label: 'P', value: '16px', level: 'p' },
  ];

  // Custom font size handler
  const handleFontSizeChange = (size) => {
    setCurrentFontSize(size);
    editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
  };

  // Custom line height handler
  const handleLineHeightChange = (height) => {
    setCurrentLineHeight(height);
    editor.chain().focus().setMark('textStyle', { lineHeight: height }).run();
  };

  // Custom letter spacing handler
  const handleLetterSpacingChange = (spacing) => {
    setCurrentLetterSpacing(spacing);
    editor.chain().focus().setMark('textStyle', { letterSpacing: spacing }).run();
  };

  // Font family handler
  const handleFontFamilyChange = (family) => {
    setCurrentFontFamily(family);
    editor.chain().focus().setFontFamily(family).run();
  };

  // Increase font size
  const increaseFontSize = () => {
    const currentSize = parseInt(currentFontSize);
    if (!isNaN(currentSize) && currentSize < 96) {
      const newSize = currentSize + 2;
      handleFontSizeChange(`${newSize}px`);
    }
  };

  // Decrease font size
  const decreaseFontSize = () => {
    const currentSize = parseInt(currentFontSize);
    if (!isNaN(currentSize) && currentSize > 8) {
      const newSize = currentSize - 2;
      handleFontSizeChange(`${newSize}px`);
    }
  };

  if (!editor) {
    return null;
  }

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      className={`toolbar-btn ${active ? 'active' : ''}`}
      title={title}
    >
      {children}
    </button>
  );

  const SelectToolbar = ({ value, onChange, options, title }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="toolbar-select"
      title={title}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );

  // Helper functions for table operations
  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const addColumnBefore = () => {
    editor.chain().focus().addColumnBefore().run();
  };

  const addColumnAfter = () => {
    editor.chain().focus().addColumnAfter().run();
  };

  const deleteColumn = () => {
    editor.chain().focus().deleteColumn().run();
  };

  const addRowBefore = () => {
    editor.chain().focus().addRowBefore().run();
  };

  const addRowAfter = () => {
    editor.chain().focus().addRowAfter().run();
  };

  const deleteRow = () => {
    editor.chain().focus().deleteRow().run();
  };

  return (
    <div className={`rich-text-editor-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
      {label && <label className="editor-label">{label}</label>}
      
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
            <u>U</u>
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
            <s>S</s>
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
            H1
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
            H2
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
            H3
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph')} title="Paragraph">
            ¶
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left">
            ⬅
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center">
            ⬌
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right">
            ➡
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify">
            ☰
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
            • List
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
            1. List
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Line">
            ──
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
            “ ”
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block">
            &lt;/&gt;
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <div className="font-size-controls">
            <ToolbarButton onClick={decreaseFontSize} title="Decrease Font Size">
              A-
            </ToolbarButton>
            <SelectToolbar
              value={currentFontSize}
              onChange={handleFontSizeChange}
              options={fontSizes}
              title="Font Size"
            />
            <ToolbarButton onClick={increaseFontSize} title="Increase Font Size">
              A+
            </ToolbarButton>
          </div>
        </div>

        <div className="toolbar-group">
          <SelectToolbar
            value={currentFontFamily}
            onChange={handleFontFamilyChange}
            options={fontFamilies}
            title="Font Family"
          />
        </div>

        <div className="toolbar-group">
          <SelectToolbar
            value={currentLineHeight}
            onChange={handleLineHeightChange}
            options={lineHeights}
            title="Line Height"
          />
        </div>

        <div className="toolbar-group">
          <SelectToolbar
            value={currentLetterSpacing}
            onChange={handleLetterSpacingChange}
            options={letterSpacings}
            title="Letter Spacing"
          />
        </div>

        <div className="toolbar-group">
          <input
            type="color"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            value={editor.getAttributes('textStyle').color || '#000000'}
            className="toolbar-color"
            title="Text Color"
          />
          <input
            type="color"
            onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
            className="toolbar-color"
            title="Background Highlight"
          />
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => {
            const url = prompt('Enter URL:');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }} active={editor.isActive('link')} title="Add Link">
            🔗
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} title="Remove Link">
            🔗❌
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={insertTable} title="Insert Table">
            📊
          </ToolbarButton>
          <ToolbarButton onClick={addColumnBefore} title="Add Column Before">
            ➕⬅
          </ToolbarButton>
          <ToolbarButton onClick={addColumnAfter} title="Add Column After">
            ➕➡
          </ToolbarButton>
          <ToolbarButton onClick={deleteColumn} title="Delete Column">
            🗑️⬇
          </ToolbarButton>
          <ToolbarButton onClick={addRowBefore} title="Add Row Before">
            ➕⬆
          </ToolbarButton>
          <ToolbarButton onClick={addRowAfter} title="Add Row After">
            ➕⬇
          </ToolbarButton>
          <ToolbarButton onClick={deleteRow} title="Delete Row">
            🗑️➡
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
            ↩ Undo
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
            ↪ Redo
          </ToolbarButton>
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={() => fileInputRef.current?.click()} title="Upload Image">
            🖼 Image
          </ToolbarButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />
        </div>

        <div className="toolbar-group">
          <ToolbarButton onClick={togglePreview} title="Preview Mode">
            👁 Preview
          </ToolbarButton>
          <ToolbarButton onClick={toggleFullscreen} title="Fullscreen">
            ⛶
          </ToolbarButton>
        </div>
      </div>

      <div className={`editor-container ${isPreview ? 'preview-mode' : ''}`}>
        <EditorContent editor={editor} className="editor-content-area" />
      </div>

      <style>{`
        .rich-text-editor-wrapper {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          transition: all 0.3s;
        }

        .rich-text-editor-wrapper.fullscreen {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9999;
          border-radius: 0;
        }

        .editor-label {
          display: block;
          font-weight: 600;
          margin-bottom: 12px;
          color: #1e293b;
          font-size: 14px;
        }

        .editor-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 12px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .toolbar-group {
          display: flex;
          gap: 4px;
          padding: 0 8px;
          border-right: 1px solid #cbd5e1;
        }

        .toolbar-group:last-child {
          border-right: none;
        }

        .toolbar-btn {
          padding: 6px 12px;
          background: #fff;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
          color: #475569;
        }

        .toolbar-btn:hover {
          background: #e2e8f0;
          border-color: #94a3b8;
        }

        .toolbar-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: #fff;
        }

        .toolbar-select {
          padding: 6px 8px;
          background: #fff;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          color: #475569;
        }

        .toolbar-color {
          width: 36px;
          height: 32px;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          cursor: pointer;
          padding: 2px;
        }

        .font-size-controls {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .editor-container {
          min-height: 400px;
          max-height: 600px;
          overflow-y: auto;
        }

        .preview-mode .rich-text-editor-content {
          background: #f8fafc;
          cursor: default;
        }

        .rich-text-editor-content {
          padding: 20px;
          min-height: 400px;
          outline: none;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }

        /* Content Styling */
        .rich-text-editor-content h1 {
          font-size: 32px;
          font-weight: 700;
          margin: 24px 0 16px;
          color: #1e293b;
        }

        .rich-text-editor-content h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 20px 0 12px;
          color: #1e293b;
        }

        .rich-text-editor-content h3 {
          font-size: 24px;
          font-weight: 600;
          margin: 16px 0 10px;
          color: #1e293b;
        }

        .rich-text-editor-content h4 {
          font-size: 20px;
          font-weight: 600;
          margin: 14px 0 10px;
          color: #1e293b;
        }

        .rich-text-editor-content h5 {
          font-size: 18px;
          font-weight: 600;
          margin: 12px 0 8px;
          color: #1e293b;
        }

        .rich-text-editor-content h6 {
          font-size: 16px;
          font-weight: 600;
          margin: 10px 0 8px;
          color: #1e293b;
        }

        .rich-text-editor-content p {
          margin: 0 0 12px;
          line-height: inherit;
        }

        .rich-text-editor-content ul,
        .rich-text-editor-content ol {
          margin: 0 0 12px 24px;
        }

        .rich-text-editor-content li {
          margin: 4px 0;
          line-height: inherit;
        }

        .rich-text-editor-content blockquote {
          border-left: 4px solid #3b82f6;
          margin: 16px 0;
          padding: 12px 20px;
          background: #f1f5f9;
          font-style: italic;
        }

        .rich-text-editor-content code {
          background: #e2e8f0;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.9em;
        }

        .rich-text-editor-content pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 16px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 16px 0;
        }

        .rich-text-editor-content pre code {
          background: none;
          color: inherit;
          padding: 0;
        }

        .rich-text-editor-content a {
          color: #3b82f6;
          text-decoration: underline;
        }

        .rich-text-editor-content a:hover {
          color: #2563eb;
        }

        .editor-image {
          max-width: 100%;
          height: auto;
          margin: 16px 0;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        /* Table Styling */
        .rich-text-editor-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
        }

        .rich-text-editor-content th,
        .rich-text-editor-content td {
          border: 1px solid #cbd5e1;
          padding: 8px 12px;
          text-align: left;
        }

        .rich-text-editor-content th {
          background: #f1f5f9;
          font-weight: 600;
        }

        /* Placeholder */
        .rich-text-editor-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .editor-toolbar {
            overflow-x: auto;
            flex-wrap: nowrap;
            -webkit-overflow-scrolling: touch;
          }

          .toolbar-group {
            flex-shrink: 0;
          }

          .toolbar-btn,
          .toolbar-select {
            font-size: 11px;
            padding: 4px 8px;
          }

          .rich-text-editor-content {
            padding: 16px;
          }

          .rich-text-editor-content h1 {
            font-size: 24px;
          }

          .rich-text-editor-content h2 {
            font-size: 20px;
          }

          .rich-text-editor-content h3 {
            font-size: 18px;
          }
        }

        @media (max-width: 480px) {
          .toolbar-btn,
          .toolbar-select {
            font-size: 10px;
            padding: 3px 6px;
          }

          .rich-text-editor-content {
            padding: 12px;
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;